import type { TModelNumberSummary } from "types/modelNumbers.type"
import type { TCustomModelNumberSummary } from "types/customModelNumbers.type"

export type TCoordinates = {
	x0: number
	y0: number
	x1: number
	y1: number
}

export type TCategory = {
	id: number
	name: string
	model_label: string
}

export type TSuperCategory = {
	id: number
	name: string | "Countertops"
}

export type TSelectedModelNumber = {
	id: number
	model_number: string
	image_from_stevens_catalogue: string | null
	dimensions: {
		width: string[]
		height: string[]
		depth: string[]
		min_size: number
		avg_size: number
		max_size: number
	}
}

export type TErefEstimationConfig = {
	eref_id: number
	series_override_id: number | null
	has_locks: boolean
	has_finished_backs: boolean
	has_slope_top: boolean
	has_separate_base: boolean
	has_plam_soffits: boolean
	case_color_id: number | null
	countertop_series_and_colors_id: number | null
	delivery_date: { id: number; date: string; project_name?: string | null } | null
}

/**
 * Delivery date as returned on a Furniture: its own when it carries an override, otherwise the
 * one of the elevation it is connected to. `is_inherited` tells the two apart so the UI never has
 * to compare values itself (REQ-407..410).
 */
export type TFurnitureDeliveryDate = {
	id: number
	date: string
	project_name: string | null
	is_inherited: boolean
}

/**
 * Countertop series and colors as returned on a Furniture (REQ-405): its own when it carries an
 * override, otherwise the one of the elevation it is connected to — same `is_inherited` shape as
 * {@link TFurnitureDeliveryDate}. `null` on the Furniture when neither the object nor its elevation
 * has one, or when the object is not a countertop.
 */
export type TFurnitureCountertopSeries = {
	id: number
	name: string
	is_inherited: boolean
}

/**
 * REQ-449. Grommets / Wall Cleats counts on a countertop Furniture (`FurnitureCountertopAttachments`
 * in the spec) — both default to 0, range 0..20. `furniture_id` is optional here only so fixtures
 * and locally-built values need not repeat the owning id; the backend always sends it.
 */
export type TFurnitureCountertopAttachments = {
	furniture_id?: number
	grommets: number
	wall_cleats: number
}

export type TErefStatus = "work_in_progress" | "reviewed" | "in_quotation"

export type TEref = {
	id: number
	version: number
	elevation_number: string | null
	/**
	 * The elevation's name without its number (REQ-474), `null` when it has none. Required on the
	 * wire; optional here because the eref fixtures across the test suite predate it.
	 */
	elevation_name?: string | null
	page_name: string
	page_number: number
	/**
	 * How many times the elevation is referenced on the drawing (REQ-423). Seeded at quotation
	 * creation from the connected ecall elevations, then edited by the user only — nothing on the
	 * frontend derives it from the current ecall count. The backend marks it `required` and never
	 * null on both `QuotationRelatedEref` and `ErefResponse`; it is optional here for the same
	 * reason `status` and `estimation_config` are — the eref fixtures across the test suite predate
	 * it, and `tsconfig.json`'s `"include": ["src"]` type-checks every one of them.
	 */
	times_referenced?: number
	coordinates: TCoordinates
	estimation_config?: TErefEstimationConfig
	status?: TErefStatus
	estimation_config_comment?: string | null
}

export type TElevationNumber = {
	id: number
	version?: number
	ecall_id?: number
	elevation_number: string
	eref_id: number | null
	reference_label?: string | null
}

export type TEcall = {
	id: number
	version: number
	page_name: string
	page_number: number
	location_page_name: string | null
	coordinates: TCoordinates
	room_name: string | null
	elevation_numbers: TElevationNumber[]
}

export type TFurnitureEstimationConfig = {
	furniture_id: number
	number_of_fillers: number
	number_of_finishing_ends: number
	lock_count: number | null
	has_locks: boolean
	has_finished_backs: boolean
	adjusted_shelves: number | null
}

export type TFurnitureStatus = "work_in_progress" | "reviewed" | "in_quotation"

export const STATUS_LABEL: Record<TErefStatus | TFurnitureStatus, string> = {
	work_in_progress: "Need review",
	reviewed: "Reviewed",
	in_quotation: "In quotation",
}

/**
 * #1346 / REQ-434..437 — the config that MAKES an object a custom object. Present (with a possibly null
 * `custom_model_number`) for exactly the custom objects and null for every other kind, which is
 * why `isCustomFurniture` tests this field and nothing else.
 *
 * Deliberately NOT detected via `category === "custom"`: the full furniture payload does send that
 * string, but the related-category PATCH response omits `category` altogether, so a detection keyed
 * off it would read a freshly converted object as a plain "Other".
 */
export type TFurnitureCustomObjectConfig = {
	custom_model_number: TCustomModelNumberSummary | null
}

export type TFurnitureFixedPriceConfig = {
	/**
	 * Nullable: the fixed-price endpoints accept `null` to CLEAR the price (bulk-clearing is the
	 * batch variant's main use case), so an Other object can legitimately hold a config with no
	 * price yet. Every read site already treats null as "no price set".
	 */
	fixed_price: number | null
	name: string | null
}

/**
 * REQ-413 — a manual correction of ONE object's price, with the justification that has to
 * come with it. An object has at most one, and it belongs to that object alone.
 *
 * `price_adjustment` is signed USD (negative lowers the object's price) and nullable on the
 * wire; `note` is mandatory — the requirement forbids saving a customization without it.
 */
export type TFurnitureCustomization = {
	price_adjustment: number | null
	note: string
}

export type TFurniture = {
	id: number
	version: number
	status?: TFurnitureStatus
	page_number: number
	eref_id: number | null
	coordinates: TCoordinates
	subclass_description_from_ml: string
	detected_model_numbers: string[]
	selected_model_number: TSelectedModelNumber | null
	height: number | null
	width: number | null
	depth: number | null
	estimation_config?: TFurnitureEstimationConfig | null
	estimation_config_comment?: string | null
	fixed_price_config?: TFurnitureFixedPriceConfig | null
	/**
	 * #1346. Non-null for a custom object, null for every other kind. Optional here — like the
	 * configs above — so the fixtures and partial casts that predate it still satisfy the type.
	 */
	custom_object_config?: TFurnitureCustomObjectConfig | null
	/**
	 * REQ-413. Arrives with the normal quotation payload (required + nullable on the wire), so
	 * no extra GET is needed and the note survives a reload for free. Optional here so the
	 * fixtures and partial casts that predate it still satisfy the type.
	 */
	customization?: TFurnitureCustomization | null
	category: TCategory | null
	super_category: TSuperCategory | null
	top_level_category: TSuperCategory | null
	// null when neither this object nor the elevation it is connected to has a delivery date.
	delivery_date?: TFurnitureDeliveryDate | null
	// REQ-405. null when neither this object nor its elevation has one, or when the object is not a
	// countertop. Optional — like delivery_date above — so fixtures/partial casts that predate it
	// still satisfy the type.
	countertop_series_and_colors?: TFurnitureCountertopSeries | null
	/**
	 * REQ-449. `null` when the object is not a countertop or its config has not been created yet.
	 * Optional so fixtures/partial casts that predate it still satisfy the type.
	 */
	countertop_attachments?: TFurnitureCountertopAttachments | null
}

// REQ-400: persisted highlights (drawn by #1323 figures / #1324 freehand), merged into one array
// by the backend and told apart by `shape_type`. Purely load/render data here — no create/delete.
export type THighlightShapeType = "rectangle" | "ellipse" | "freehand"

export type TFigureHighlight = {
	id: number
	shape_type: "rectangle" | "ellipse"
	page_number: number
	coordinates: TCoordinates
	color: string
	fill_color: string | null
	opacity: number
	stroke_width: number
	line_style: string
	created_by: string | null
	created_at: string
}

export type TFreehandHighlight = {
	id: number
	shape_type: "freehand"
	page_number: number
	coordinates: TCoordinates
	color: string
	// List of strokes; each stroke is a list of [x, y] point pairs.
	path: number[][][]
	stroke_width: number
	opacity: number
	created_by: string | null
	created_at: string
}

export type THighlight = TFigureHighlight | TFreehandHighlight

/**
 * Body for `POST /quotations/{quotation_id}/highlights/figures/` (REQ-400 create). Every field is
 * required except `fill_color` (nullable — null means a transparent fill). `color` (stroke) and
 * `fill_color` are independently settable — verified via `pnpm run api:show`, not one derived from
 * the other — matching the toolbar's separate "Color" / "Fill color" pickers. `x1 >= x0` / `y1 >=
 * y0`, backend rect space, `page_number` 0-based. Response is `TFigureHighlight`
 * (id/created_by/created_at added, no `version` — the backend model has none).
 */
export type TCreateFigureHighlightRequest = {
	shape_type: "rectangle" | "ellipse"
	x0: number
	y0: number
	x1: number
	y1: number
	page_number: number
	color: string
	fill_color: string | null
	opacity: number
	stroke_width: number
	line_style: string
}

// REQ-395/396: a free-text note (bbox + text + font size + text color) placed directly on a page.
// The schema marks `notes` required on the GET response (unlike `highlights`, which predates this
// field and stays optional for backward compatibility) — but it's kept optional here too, for the
// SAME reason `highlights?` is: fixtures across this test suite predate it, and `tsconfig.json`'s
// `"include": ["src"]` type-checks every one of them. Treat a missing value as `[]`.
export type TNote = {
	id: number
	version: number
	page_number: number
	coordinates: TCoordinates
	text: string
	font_size: number
	text_color: string
}

export type TCreateNoteRequest = {
	x0: number
	y0: number
	x1: number
	y1: number
	page_number: number
	text: string
	font_size: number
	text_color: string
}

// Every field but `version` is optional and independently settable — a left-out field keeps its
// stored value (verified via `pnpm run api:show`). `x0/y0/x1/y1` must be sent as a full set or not
// at all (partial → 400), which the commit flow that builds this body must respect.
export type TUpdateNoteRequest = {
	version: number
	x0?: number
	y0?: number
	x1?: number
	y1?: number
	text?: string
	font_size?: number
	text_color?: string
}

/**
 * Body for `POST /quotations/{quotation_id}/highlights/freehand/` (REQ-400 create, #1324). Every
 * field is required — note there is no `fill_color` and no `line_style` here, unlike the figure
 * request: an Ink annotation has no fill concept, and the freehand endpoint accepts neither field
 * (verified via `pnpm run api:show`).
 *
 * `path` is a list of STROKES, each a list of `[x, y]` pairs — the shape Apryse's
 * `FreeHandAnnotation.getPaths()` already produces, in backend (un-rotated) page space. A single
 * highlight carries several strokes whenever the user draws them within the create tool's
 * `createDelay` window.
 *
 * `x0`/`y0`/`x1`/`y1` are the bounding box DERIVED FROM `path` (the endpoint documents them exactly
 * that way and requires them, so there is no "let the backend derive it" option). They are the raw
 * min/max of the points, deliberately NOT inflated by half the stroke width: the point of sending a
 * bbox the backend would itself derive the same way is that the two can never disagree.
 */
export type TCreateFreehandHighlightRequest = {
	x0: number
	y0: number
	x1: number
	y1: number
	page_number: number
	color: string
	path: number[][][]
	stroke_width: number
	opacity: number
}

export type TPageElevationData = {
	// Page-info record id + optimistic-lock version, required by the page-name PATCH
	// (`PATCH /quotations/{id}/pages/{page_info_id}/`). Optional so existing fixtures /
	// partial casts don't break; the "Set page name" edit affordance only shows when both
	// are present.
	id?: number
	version?: number
	page_number: number
	page_name: string | null
	erefs: TEref[]
	ecalls: TEcall[]
	furniture: TFurniture[]
	// Optional for the same reason as id/version above: existing fixtures predate this field.
	// The backend always sends it (empty array when a page has none) — treat a missing value as [].
	highlights?: THighlight[]
	// REQ-395/396: same optional-for-old-fixtures treatment as `highlights` above.
	notes?: TNote[]
}

export type TErefWithFurniture = TEref & {
	furniture: TFurniture[]
}

export type TFurnitureVersionRef = { id: number; version: number }

export type TConnectFurnitureRequest = {
	eref_version: number
	furniture: TFurnitureVersionRef[]
}

/**
 * Response of `POST /furniture/eref/{eref_id}/connect/` (and disconnect). Carries the eref
 * at its new version plus every furniture currently linked to that eref after the operation
 * (not just the ones just connected), each with its new version.
 */
export type TConnectFurnitureResponse = {
	eref_id: number
	eref_version: number
	furniture: TFurnitureVersionRef[]
}

/**
 * Body for `POST /quotations/{id}/furniture/eref/{eref_id}/move/` — reassign one or more furniture
 * to the target eref (in the URL) regardless of which eref they are currently linked to (or whether
 * unlinked). Same shape as connect/disconnect: `eref_version` is the TARGET eref's current version,
 * each furniture supplies its current `version`. All furniture must be on the target eref's page.
 */
export type TMoveFurnitureRequest = {
	eref_version: number
	furniture: TFurnitureVersionRef[]
}

/**
 * Response of the furniture move endpoint. `erefs` are the target eref plus every source eref that
 * lost at least one furniture, each at its new version (the target is present only when at least one
 * furniture actually moved). `furniture` is every requested item — moved ones bumped, no-ops
 * (already on the target) at their unchanged version — all now linked to the target eref.
 */
export type TMoveFurnitureResponse = {
	erefs: { id: number; version: number }[]
	furniture: TFurnitureVersionRef[]
}

/**
 * Body for `PATCH /quotations/{id}/furniture/status/` — atomically set the status of many
 * furniture at once. All three statuses are accepted; only `in_quotation` can produce per-item
 * failures (see `failed` on the response), because it is the only one with preconditions. Each
 * item supplies its current `version` for optimistic locking; a single mismatch rejects the whole
 * request with 400.
 */
export type TBatchUpdateFurnitureStatusRequest = {
	status: TFurnitureStatus
	furniture: TFurnitureVersionRef[]
}

/**
 * Response of the batch status endpoint: every furniture from the request with its new
 * `status` + `version` (items already at the target status are returned unchanged). Partial
 * furniture — merge into the cache via `spliceFurnitureIntoCache`.
 *
 * `failed` groups the items that did NOT meet a precondition of the requested status, keyed by
 * reason; each listed item was left untouched, version included. Always empty (or absent) for
 * `work_in_progress` / `reviewed`.
 */
export type TBatchUpdateFurnitureStatusResponse = {
	furniture: { id: number; status: TFurnitureStatus; version: number }[]
	failed?: Record<string, number[]>
}

/**
 * One distinct width/height/depth triple across a furniture selection, plus how many objects in
 * that selection carry it. Rendered by the batch model-number modal's "Cabinet dimensions" block
 * (REQ-403) and used as its match target set: a candidate's axis counts as in range only when
 * EVERY triple fits it.
 */
export type TDimensionGroup = {
	width: number | null
	height: number | null
	depth: number | null
	count: number
}

/**
 * Body for `PATCH /quotations/{id}/furniture/` — the batch counterpart of the single-furniture
 * PATCH (REQ-403). Exactly one of `selected_model_number_id` / `name` / `height` / `width` /
 * `depth` is required (`anyOf` on the backend); an omitted field leaves every item's stored value
 * untouched, which is what makes a per-field commit safe on a selection holding differing values.
 *
 * `selected_model_number_id` is NOT nullable here — the batch endpoint cannot clear a selection —
 * and requires every listed item to share the model number's category. `name` applies to Other /
 * fixed-price furniture only, may be null, and rejects a blank string. All items must be on one page.
 */
export type TBatchUpdateFurnitureRequest = {
	furniture: TFurnitureVersionRef[]
	selected_model_number_id?: number
	name?: string | null
	height?: number
	width?: number
	depth?: number
	/** Omit to leave unchanged, `null` to clear the override (item goes back to inheriting), an id to set one. */
	delivery_date_id?: number | null
}

/** Response of the batch furniture PATCH: the full payload of every requested item. */
export type TBatchUpdateFurnitureResponse = {
	furniture: TFurniture[]
}

/**
 * Body for `PATCH /quotations/{id}/furniture/related-category/` — move a whole selection to one
 * category. `related_category_id` is required and nullable; `null` moves every listed item to
 * Other / fixed-price mode. An item already in the target category keeps its version.
 */
/**
 * The value `related_category_id` may carry, single-item or batch. A category id moves the object
 * into that category; `null` makes it an "Other" (fixed-price) object; the literal string
 * `"custom"` (#1346) makes it a CUSTOM object — the backend clears its category, disconnects its
 * Stevens model number, drops its estimation / fixed-price config and creates an empty
 * `custom_object_config`. Moving away from `"custom"` afterwards undoes that.
 */
export type TRelatedCategoryValue = number | "custom" | null

export type TBatchUpdateFurnitureCategoryRequest = {
	furniture: TFurnitureVersionRef[]
	related_category_id: TRelatedCategoryValue
}

/**
 * Response of the batch category PATCH. `ordering` is a LIST — a selection can span several
 * groups (one per eref any item is connected to, plus one for the unconnected page objects), so
 * every group has to be re-sorted. `model_numbers` are the target category's model numbers,
 * returned once for the whole batch (empty when the category was cleared); the viewer doesn't
 * consume them — the model-number picker fetches its own.
 */
export type TBatchUpdateFurnitureCategoryResponse = {
	furniture: TFurniture[]
	model_numbers: TModelNumberSummary[]
	ordering: TFurnitureOrdering[]
}

/**
 * Body for `PATCH /quotations/{id}/furniture/select-arbitrary-model-number/` — the batch
 * counterpart of the single-item arbitrary pick (REQ-398). The model number may belong to ANY
 * category but must be connected to one, and the backend moves every listed item's category to
 * match in the same transaction. This is the path a mixed-category selection has to take, since
 * the plain batch PATCH demands one shared category.
 */
export type TBatchSelectArbitraryModelNumberRequest = {
	furniture: TFurnitureVersionRef[]
	selected_model_number_id: number
}

/** Response of the batch arbitrary-model-number PATCH — same shape rules as the category batch. */
export type TBatchSelectArbitraryModelNumberResponse = {
	furniture: TFurniture[]
	ordering: TFurnitureOrdering[]
}

/**
 * Body for `PATCH /quotations/{id}/furniture/configuration/` — the batch attachments config.
 * Each of the five fields is individually optional (omit to leave every item's stored value
 * alone) but at least one must be present. A shared category is NOT required; countertops and
 * Other objects are rejected outright, which is why the panel only offers this section when every
 * selected object is a cabinet.
 */
export type TBatchUpdateFurnitureConfigRequest = {
	furniture: TFurnitureVersionRef[]
	number_of_fillers?: 0 | 1 | 2
	number_of_finishing_ends?: 0 | 1 | 2
	has_locks?: boolean
	has_finished_backs?: boolean
	adjusted_shelves?: number
}

/**
 * The subset of the batch configuration body that is actual field data — the attachments patch a
 * single row of the batch panel commits, without the `furniture` list the hook fills in.
 */
export type TBatchConfigPatch = Omit<TBatchUpdateFurnitureConfigRequest, "furniture">

/**
 * Response of the batch configuration PATCH: the whole resulting config per item, keyed by
 * `furniture_id` (not `id` — the single-item endpoint's shape). No `ordering`: attachments don't
 * affect render order.
 */
export type TBatchUpdateFurnitureConfigResponse = {
	furniture: {
		furniture_id: number
		version: number
		has_locks: boolean
		has_finished_backs: boolean
		number_of_fillers: number
		number_of_finishing_ends: number
		adjusted_shelves: number
	}[]
}

/**
 * Body for `PATCH /quotations/{id}/furniture/countertop-attachments/` (REQ-449, batch):
 * all-or-nothing transactional, countertop-only, single-page-only. Each count independently
 * optional — a count left out keeps every item's current value.
 */
export type TBatchUpdateFurnitureCountertopAttachmentsRequest = {
	furniture: TFurnitureVersionRef[]
	grommets?: number
	wall_cleats?: number
}

/**
 * The field-only subset of the batch countertop-attachments body — the patch a single row of the
 * batch panel commits, without the `furniture` list the hook fills in. Mirrors `TBatchConfigPatch`.
 */
export type TBatchCountertopAttachmentsPatch = Omit<TBatchUpdateFurnitureCountertopAttachmentsRequest, "furniture">

/**
 * Response of the batch countertop-attachments PATCH: `furniture_id` keyed, one row per requested
 * item, in request order. The counts are NESTED under `countertop_attachments`, not flat on the row.
 * No `ordering` — attachments don't affect render order.
 */
export type TBatchUpdateFurnitureCountertopAttachmentsResponse = {
	furniture: { furniture_id: number; version: number; countertop_attachments: TFurnitureCountertopAttachments }[]
}

/**
 * Body for `PATCH /quotations/{id}/furniture/fixed-price/` — one fixed price for the whole
 * selection. `fixed_price` is required and nullable (`null` bulk-clears). Applies ONLY to items
 * that are already Other / fixed-price objects; anything else rejects the entire batch.
 */
export type TBatchUpdateFurnitureFixedPriceRequest = {
	furniture: TFurnitureVersionRef[]
	fixed_price: number | null
}

/** Response of the batch fixed-price PATCH — `furniture_id` keyed, no `ordering`. */
export type TBatchUpdateFurnitureFixedPriceResponse = {
	furniture: { furniture_id: number; version: number; fixed_price: number | null }[]
}

/**
 * Body for `PUT /quotations/{id}/furniture/comment/` — writes one estimation comment to every
 * listed item, creating or overwriting per item. May mix furniture types (a comment is free text).
 * An item whose stored comment already equals `comment` keeps its version.
 */
export type TBatchUpdateFurnitureCommentRequest = {
	furniture: TFurnitureVersionRef[]
	comment: string
}

/** Response of the batch comment PUT — `id` keyed (unlike the config/fixed-price batches). */
export type TBatchUpdateFurnitureCommentResponse = {
	furniture: { id: number; version: number; comment: string }[]
}

/**
 * Response of `DELETE /quotations/{id}/furniture/comment/`. The batch is passed as repeated
 * `items=<id>:<version>` query params (a DELETE carries no body) — the same scheme
 * `furniture/batch-delete/` uses. An item that has no comment is a no-op that keeps its version
 * rather than failing the batch, unlike the single-item endpoint's 404.
 */
export type TBatchDeleteFurnitureCommentResponse = {
	furniture: { id: number; version: number }[]
}

/**
 * Body for `PUT /quotations/{id}/furniture/customization/` (REQ-431) — writes ONE
 * `price_adjustment`/`note` pair to every listed item, creating the customization where an item
 * has none and OVERWRITING it where it does (the batch counterpart of
 * `PUT …/furniture/{furniture_id}/customization/`, REQ-413/414). Both fields are required by the
 * endpoint; `note` may be an empty string and `price_adjustment` may be `null`. An item whose
 * stored customization already equals the request keeps its version.
 */
export type TBatchUpdateFurnitureCustomizationRequest = {
	furniture: TFurnitureVersionRef[]
	price_adjustment: number | null
	note: string
}

/** Response of the batch customization PUT — `id` keyed, one row per requested item. */
export type TBatchUpdateFurnitureCustomizationResponse = {
	furniture: { id: number; version: number; price_adjustment: number | null; note: string }[]
}

/**
 * Response of `DELETE /quotations/{id}/furniture/customization/` (REQ-431). The batch travels as
 * repeated `items=<id>:<version>` query params, same scheme as the comment DELETE — a DELETE
 * carries no body. An item with no customization is a no-op that keeps its version, rather than
 * failing the batch.
 */
export type TBatchDeleteFurnitureCustomizationResponse = {
	furniture: { id: number; version: number }[]
}

/**
 * Response of `DELETE /quotations/{id}/furniture/batch-delete/`: every eref affected by the
 * deletion in its post-delete state (new version + connectivity). Empty when none of the
 * deleted furniture was connected to an eref.
 */
export type TBatchDeleteFurnitureResponse = {
	updated_erefs: TEref[]
}

/**
 * Body for `POST /quotations/{id}/eref/` — create a new eref by drawing its area on the drawing
 * (REQ-292). Every field is required by the backend: `page_number` is 0-based, `page_name` is the
 * name of that page (max 16 chars) and the coordinates are in the same backend space the rendered
 * eref/furniture rects use. The backend rejects a duplicate `(page_number, x0, y0, x1, y1)` and a
 * duplicate `(page_number, elevation_number)` with a 400 `non_field_errors`.
 */
export type TCreateErefRequest = {
	elevation_number: string
	page_name: string
	page_number: number
	x0: number
	y0: number
	x1: number
	y1: number
}

/**
 * Body for `POST /quotations/{id}/furniture/` — creates a furniture from a rectangle drawn on the
 * quotation canvas. Shared by the countertop (REQ-298) and cabinet (REQ-293) create flows.
 *
 * The area, `page_number` and `related_category_id` are the only fields the endpoint requires;
 * `related_category_id` is nullable, and null is how an "Other" item is created. The dimensions and
 * `selected_model_number_id` are optional — the cabinet panel leaves either for the object's own
 * sidebar — and a model number must belong to `related_category_id` or the endpoint rejects the
 * pair. `eref_id` + `eref_version` are sent together, and the eref must be on the area's page.
 */
export type TCreateFurnitureRequest = {
	page_number: number
	x0: number
	y0: number
	x1: number
	y1: number
	related_category_id: number | null
	eref_id?: number | null
	eref_version?: number
	width?: number
	height?: number
	depth?: number
	selected_model_number_id?: number | null
}

export type TUpdateErefRequest = {
	version: number
	elevation_number?: string
	/** 1..128 characters; `null` clears the name. */
	elevation_name?: string | null
	page_name?: string
	page_number?: number
	/** Integer, 0..99 — the endpoint rejects anything else. */
	times_referenced?: number
	x0?: number
	y0?: number
	x1?: number
	y1?: number
}

/** Full ecall returned by endpoints that disconnect it; mirrors `TEcall` plus a `quotation_id`. */
export type TDisconnectedEcall = TEcall & {
	quotation_id?: number
}

/**
 * Response of editing an eref (e.g. its elevation number). Embeds the updated
 * eref plus the ecalls / ecall_elevations the backend disconnected from it,
 * each carrying its post-disconnection `version`. Both lists are empty when
 * nothing was disconnected.
 */
export type TUpdateErefResponse = TEref & {
	disconnected_ecalls: TDisconnectedEcall[]
	disconnected_ecall_elevations: TElevationNumber[]
}

export type TUpdateErefConfigurationRequest = {
	version: number
	series_override_id?: number | null
	has_locks?: boolean
	has_finished_backs?: boolean
	has_slope_top?: boolean
	has_separate_base?: boolean
	has_plam_soffits?: boolean
	case_color_id?: number | null
	countertop_series_and_colors_id?: number | null
	delivery_date_id?: number | null
}

export type TUpdateErefConfigurationResponse = {
	id: number
	version: number
	configuration: Omit<TErefEstimationConfig, "eref_id">
	updated_furniture_list: TFurniture[]
}

export type TUpdateErefStatusRequest = {
	version: number
	status: TErefStatus
}

/**
 * Response of `DELETE /quotations/{id}/eref/{erefId}/` (REQ-376). Deleting an eref does NOT
 * delete what was attached to it — the backend detaches the connected furniture and ecall
 * elevations, bumps their versions, and returns them in their post-delete state (full payloads,
 * `eref_id: null`). Both lists are empty when the eref had nothing connected. The endpoint takes
 * no `version` — delete is not under optimistic locking.
 */
export type TErefDeleteResponse = {
	updated_ecalls: TEcall[]
	updated_furniture_list: TFurniture[]
}

export type TUpdateFurnitureCategoryRequest = {
	version: number
	related_category_id: TRelatedCategoryValue
}

export type TUpdateFurnitureFixedPriceRequest = {
	version: number
	fixed_price: number
}

/**
 * Body for `PATCH …/furniture/{furniture_id}/countertop-series/` (REQ-405) — a dedicated endpoint,
 * not the general furniture PATCH, which does not (and should not) carry this field. `null` clears
 * this object's own override and falls it back to inheriting its elevation's series and colors.
 * Only valid for countertop furniture; the endpoint 400s otherwise.
 */
export type TUpdateFurnitureCountertopSeriesRequest = {
	version: number
	countertop_series_and_colors_id: number | null
}

export type TUpdateFurnitureCountertopSeriesResponse = {
	id: number
	version: number
	countertop_series_and_colors: TFurnitureCountertopSeries | null
}

/**
 * Body for `PATCH …/furniture/{furniture_id}/countertop-attachments/` (REQ-449). Each
 * count is independently optional, so one can change without sending the other. Only valid for
 * countertop furniture; the endpoint 400s otherwise, same rule as countertop series.
 */
export type TUpdateFurnitureCountertopAttachmentsRequest = {
	version: number
	grommets?: number
	wall_cleats?: number
}

/** Keyed by `id` (not `furniture_id`), with the counts NESTED under `countertop_attachments`. */
export type TUpdateFurnitureCountertopAttachmentsResponse = {
	id: number
	version: number
	countertop_attachments: TFurnitureCountertopAttachments
}

export type TUpdateFurnitureRequest = {
	version: number
	selected_model_number_id?: number | null
	x0?: number
	y0?: number
	x1?: number
	y1?: number
	height?: number | null
	width?: number | null
	depth?: number | null
	name?: string | null
	// Overrides the elevation's delivery date. `null` drops the override (object follows its
	// elevation again); omit to leave the current assignment unchanged.
	delivery_date_id?: number | null
}

/**
 * Body for `PATCH …/furniture/{id}/select-arbitrary-model-number/` (REQ-398).
 *
 * Unlike `TUpdateFurnitureRequest`, `selected_model_number_id` may belong to ANY
 * category — the backend moves the furniture's `related_category` to match in the
 * same transaction. It is required (this endpoint cannot clear a selection; pass
 * `selected_model_number_id: null` through `updateFurniture` for that).
 */
export type TSelectArbitraryModelNumberRequest = {
	version: number
	selected_model_number_id: number
}

/**
 * Body for `PATCH …/furniture/{id}/custom-model-number/`. Links (or clears, with
 * `custom_model_number_id: null`) a custom model number. If the furniture is not already a
 * custom object, this call converts it first — same effect as the `"custom"` branch of
 * `related-category`, in one request.
 */
export type TLinkFurnitureCustomModelNumberRequest = {
	version: number
	custom_model_number_id: number | null
}

export type TUpdateFurnitureConfigRequest = {
	version: number
	lock_count?: number | null
	number_of_fillers?: number
	number_of_finishing_ends?: number
	has_locks?: boolean
	has_finished_backs?: boolean
	adjusted_shelves?: number | null
}

export type TUpdateFurnitureCommentRequest = {
	version: number
	comment: string
}

/**
 * REQ-413/414. The PUT creates the customization when the object has none and OVERWRITES both
 * values when it does — an object can only ever have one, so there is no "add a second" call.
 */
export type TUpdateFurnitureCustomizationRequest = {
	version: number
	price_adjustment: number | null
	/** Mandatory (REQ-413). Stored verbatim, line breaks included. Max 255 characters. */
	note: string
}

/** `version` is the furniture's version AFTER the write, for the next optimistic lock. */
export type TUpdateFurnitureCustomizationResponse = TFurnitureCustomization & {
	id: number
	version: number
}

/** The DELETE answers with the furniture's version after the removal, and nothing else. */
export type TDeleteFurnitureCustomizationResponse = {
	version: number
}

export type TUpdateFurnitureStatusRequest = {
	version: number
	status: TFurnitureStatus
}

/**
 * Render order for a single furniture group, returned by the furniture PATCH
 * endpoints after backend `parent_eref_id` is the eref the furnitures
 * belong to, or `null` when they are not connected to an eref. The order is
 * decided server-side; the client must render `furnitures_ids_in_order` as-is.
 */
export type TFurnitureOrdering = {
	page_number: number
	parent_eref_id: number | null
	furnitures_ids_in_order: number[]
}

/**
 * Response shape of `PATCH /furniture/{id}/` and
 * `PATCH /furniture/{id}/related-category/` after backend #1767: the updated
 * furniture plus the backend-decided render order for its affected group.
 */
export type TUpdateFurnitureResponse = {
	this_furniture: TFurniture
	ordering: TFurnitureOrdering
}

/**
 * Body for `PATCH /quotations/{id}/furniture/custom-model-number/` — the batch counterpart, linking
 * one definition to every listed item and converting any that is not custom yet.
 */
export type TBatchUpdateFurnitureCustomModelNumberRequest = {
	furniture: TFurnitureVersionRef[]
	custom_model_number_id: number | null
}

/**
 * Response of the batch custom-model-number PATCH. Shaped like the batch arbitrary-model-number
 * one: every updated item in full, plus one `ordering` group per eref the selection touches —
 * converting to a custom object changes the render ordering, so the groups have to be re-sorted.
 */
export type TBatchUpdateFurnitureCustomModelNumberResponse = {
	furniture: TFurniture[]
	ordering: TFurnitureOrdering[]
}

/**
 * Body for `POST /quotations/{id}/furniture/{furnitureId}/duplicate/` — creates a new
 * furniture from the source identified in the URL. Only the new top-left (`x0`, `y0`) and
 * target `page_number` are sent; the backend derives `x1`/`y1` from the source's size.
 *
 * `eref_id` + `eref_version` are sent together ONLY when pasting onto the source eref's own
 * page (erefs are page-bound). When omitted the new furniture is created unassigned
 * (`eref_id = null`). When supplied, `eref_id` must equal the source's `eref_id` and
 * `eref_version` must match the source eref's current version; the backend links the copy
 * and bumps that eref's version by 1.
 */
export type TDuplicateFurnitureRequest = {
	x0: number
	y0: number
	page_number: number
	eref_id?: number
	eref_version?: number
}

/**
 * Response of the furniture duplicate endpoint: the created furniture (`this_furniture`), the
 * backend-decided render order for its group (`ordering`, same shape as the PATCH responses),
 * plus — when the copy was linked to an eref — that eref at its incremented version.
 */
export type TDuplicateFurnitureResponse = {
	this_furniture: TFurniture
	ordering: TFurnitureOrdering
	updated_eref?: TEref
}

/** Response of the furniture create endpoint. */
export type TCreateFurnitureResponse = {
	this_furniture: TFurniture
	ordering: TFurnitureOrdering
	updated_eref: TEref | null
}

export type TUpdateErefCommentRequest = {
	version: number
	comment: string
}

export type TUpdateEcallRequest = {
	version: number
	x0?: number
	y0?: number
	x1?: number
	y1?: number
	room_name?: string
	page_name?: string
	page_number?: number
}

export type TCreateEcallRequest = {
	elevations: Array<{
		elevation_number: string
		reference_label?: string | null
		eref_id?: number | null
		eref_version?: number
	}>
	room_name?: string | null
	page_name: string
	page_number: number
	x0: number
	y0: number
	x1: number
	y1: number
}

/**
 * An eref disconnected from an ecall as a side effect of editing the ecall's
 * page or one of its elevation numbers. The backend bumps the eref's version on
 * disconnection; we only need `id` + `version` to keep the cache in sync (the
 * connection itself lives on the ecall's `elevation_numbers[].eref_id`).
 */
export type TDisconnectedEref = {
	id: number
	version: number
	/** Present when the backend sends the whole eref row (REQ-462); carried into the cache. */
	times_referenced?: number
}

/** An eref whose connections changed in a request, in its post-update state (REQ-462). */
export type TUpdatedEref = TDisconnectedEref

export type TCreateEcallResponse = TEcall & {
	updated_erefs: TUpdatedEref[]
}

export type TCreateEcallElevationResponse = TElevationNumber & {
	updated_erefs: TUpdatedEref[]
}

export type TDeleteEcallElevationResponse = {
	updated_erefs: TUpdatedEref[]
}

export type TUpdateEcallResponse = TEcall & {
	quotation_id?: number
	disconnected_erefs: TDisconnectedEref[]
}

/**
 * Response of `DELETE /quotations/{id}/ecall/{ecallId}/` (REQ-297). The backend detaches the erefs
 * that the deleted ecall's elevations pointed at — it does not delete them — and returns them with
 * their versions incremented; the list is empty when no elevation had a connected eref. The
 * payload carries whole eref rows, but only `id` + `version` are load-bearing here: the link itself
 * lives on the ecall's `elevation_numbers[].eref_id`, which disappears with the ecall row. The
 * endpoint takes no `version` — delete is not under optimistic locking.
 */
export type TEcallDeleteResponse = {
	updated_erefs: TDisconnectedEref[]
}

export type TUpdateEcallElevationsResponse = {
	elevations: TElevationNumber[]
	disconnected_erefs: TDisconnectedEref[]
	/** Every eref whose links changed (connected or disconnected), REQ-462. */
	updated_erefs?: TUpdatedEref[]
}

export type TCreateEcallElevationRequest = {
	ecall_version: number
	elevation_number: string
	eref_id?: number | null
	eref_version?: number
	reference_label?: string
}

export type TUpdateEcallElevationItem = {
	version: number
	ecall_elevation_id: number
	elevation_number: string
	eref_id?: number | null
	eref_version?: number
	/**
	 * `null` clears the label; `""` is rejected by the endpoint ("This field may not be blank.",
	 * issue #1340), which types the field `string, nullable: true`.
	 */
	reference_label?: string | null
}

export type TUpdateEcallElevationsRequest = {
	ecall_version: number
	elevations: TUpdateEcallElevationItem[]
}

export type TAutoConnectEcallElevationRequest = {
	ecall_version: number
}

export type TAutoConnectEcallElevationResponse = {
	ecall_elevation: TAutoConnectedElevation
	eref: TEref
	ecall: TEcall
}

export type TAutoConnectErefEcallsRequest = {
	eref_version: number
}

export type TAutoConnectedElevation = TElevationNumber & { version: number }

export type TAutoConnectedEcall = {
	id: number
	version: number
	connected_elevations: TAutoConnectedElevation[]
}

export type TAutoConnectErefEcallsResponse = {
	eref: TEref
	connected_ecalls: TAutoConnectedEcall[]
}

export type TGroupedPageData = {
	// Carried through from TPageElevationData so the left sidebar's per-page "Set page name"
	// edit affordance can PATCH the right page-info record. Optional — see TPageElevationData.
	id?: number
	version?: number
	page_number: number
	page_name: string | null
	erefs: TErefWithFurniture[]
	unassignedFurniture: TFurniture[]
	ecalls: TEcall[]
}

/**
 * Body for `PATCH /quotations/{id}/pages/{page_info_id}/` — partially update a page-info
 * record. `name` is the alphanumerical page designator (e.g. `A103`); `coordinates` is the
 * page detection bounding box (complete set or omitted entirely). At least one of `name` /
 * `coordinates` must be present. `version` is the current optimistic-lock version; the
 * backend rejects a mismatch with 400 and increments `version` by 1 on success.
 */
export type TUpdatePageRequest = {
	version: number
	name?: string
	coordinates?: TCoordinates
}

/** The page-info record as returned by the page PATCH endpoint. */
export type TPageInfo = {
	id: number
	version: number
	page_number: number
	page_name: string | null
	x0: number
	y0: number
	x1: number
	y1: number
}

/**
 * Response of `PATCH /quotations/{id}/pages/{page_info_id}/`. Carries the updated page-info
 * record plus the propagation the backend performed: `erefs` on that page (with the new
 * `page_name`) and any ecalls / ecall_elevations disconnected as a side effect of the edit.
 */
export type TUpdatePageResponse = {
	page_info: TPageInfo
	erefs: TEref[]
	disconnected_ecalls: TDisconnectedEcall[]
	disconnected_ecall_elevations: TElevationNumber[]
}
